import { Injectable } from '@angular/core';
import { PassEncryption } from 'src/app/models/encryption';
import { HelperService } from './helper.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class EncryptionService {

    constructor(
        private helperService: HelperService,
    ) {
    }

    // Used to convert strings into Uint8Array format
    private encoder = new TextEncoder();

    /**
     * Creates a CryptoKey from a password using SHA-256 hashing.
     * This ensures the password is converted into a 256-bit key usable for AES-CBC.
     * 
     * @param password - The password string to derive the key from.
     * @returns A Promise that resolves to a CryptoKey for encryption.
     */
    async createKey(password: string): Promise<CryptoKey> {
        const hash = await crypto.subtle.digest('SHA-256', this.encoder.encode(password));
        return crypto.subtle.importKey('raw', hash, { name: 'AES-CBC' }, false, ['encrypt']);
    }

    /**
     * Encrypts a string using AES-CBC with a derived key from the given encryptionKey.
     * A new IV is randomly generated for each encryption and prepended to the ciphertext.
     * 
     * @param input - The plaintext string to encrypt.
     * @param encryptionKey - The password used to derive the encryption key.
     * @returns A Promise that resolves to the base64-encoded string of IV + ciphertext.
     */
    async encryptString(input: string, encryptionKey: string): Promise<string> {
        const iv = crypto.getRandomValues(new Uint8Array(16));
        const key = await this.createKey(encryptionKey);
        const data = this.encoder.encode(input);
        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-CBC', iv },
            key,
            data
        );

        const result = new Uint8Array(iv.length + encrypted.byteLength);
        result.set(iv, 0);
        result.set(new Uint8Array(encrypted), iv.length);

        // Encode the combined result to a base64 string for safe transport/storage
        return btoa(String.fromCharCode(...result));
    }

    async encryptPassword(password: string): Promise<PassEncryption> {
        var splitLength = Math.floor(password.length / 3);
        var splitModule = password.length % 3;

        const passSplit = [];
        passSplit.push(password.slice(0, splitLength));
        passSplit.push(password.slice(splitLength, (passSplit[0].length + splitLength + splitModule)));
        passSplit.push(password.slice((passSplit[0].length + splitLength + splitModule), password.length));

        var randomString = this.helperService.generateRandomString(15);

        passSplit[0] = randomString.slice(0, 4) + passSplit[0] + randomString.slice(8, 11);
        passSplit[1] = randomString.slice(6, 8) + passSplit[1] + randomString.slice(10, 15);
        passSplit[2] = randomString.slice(3, 9) + passSplit[2] + randomString.slice(12, 14);

        const pass = new PassEncryption();
        pass.a1 = await this.encryptString(passSplit[2], environment.encryptionKey);
        pass.m1 = await this.encryptString(passSplit[0], environment.encryptionKey);
        pass.m2 = await this.encryptString(passSplit[1], environment.encryptionKey);
        return pass;
    }
}